import React, {Component, ReactElement} from 'react'
import NatTokenRepo, {RegisterTokenMailAddressRequest} from '../token/repository/NatTokenRepo'
import {
    ContentsSubTitleInner,
    ContentsSubTitleWrapper,
    MainContainer,
    ParagraphText,
    TxtBold,
} from '../../../styledComponents/ContentsArea'
import {StyledDiv, StyledLi} from '../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {
    DisplayBlockDiv,
    DisplayItemInput,
    DisplayItemInputLabelRequired,
    DisplayItemInputTitleRequired,
    FieldErrorLabel,
} from '../../../styledComponents/DisplayItem'
import {PrimaryStyledButton, SecondaryButton} from '../../../styledComponents/Button'
import {Colors} from '../../../styledComponents/Constants'
import {Box} from '@mui/material'
import {Messages} from '../../../Messages'
import {Nullable} from '../../../app/TypeHelpers'
import {ErrorResponseBody} from '../../../network/response/ErrorResponseBody'
import {BaseHttpError} from '../../../network/errors/baseHandling/BaseHttpError'
import {RequestItemError} from '../../../network/response/RequestItemError'
import {createResidentPage, ResidentPagePropsSet} from '../../../components/shared/hoc/createResidentPage'
import {NatAsyncCallFunction} from '../../common/util/Natutil'
import {NatPostTokenMailAddress} from '../token/repository/NatPostTokenMailAddress'
import {Location} from 'react-router-dom'

interface FieldsError {
    mailAddress: Error[]
    mailAddressConfirm: Error[]
}

interface Error {
    message: string
    hasError: boolean
}

type Field = 'mailAddress' | 'mailAddressConfirm'

interface Props {
    tokenRepo: NatTokenRepo,
    location: Location,
}

interface States {
    inputMailAddressValue: string
    inputMailAddressConfirmValue: string
    changeMailAddressButtonDispFlg: boolean
    fieldsError: Partial<FieldsError>,
}

class NatResidentChangeMailAddressPage extends Component<Props & ResidentPagePropsSet, States> {


    constructor(props: Props & ResidentPagePropsSet) {
        super(props)
        this.state = {
            inputMailAddressValue: '',
            inputMailAddressConfirmValue: '',
            changeMailAddressButtonDispFlg: false,
            fieldsError: {},
        }

        this.onChangeMailAddress = this.onChangeMailAddress.bind(this)
        this.onChangeMailAddressConfirm = this.onChangeMailAddressConfirm.bind(this)
        this.changeMailAddressButtonClick = this.changeMailAddressButtonClick.bind(this)
        this.backUserSettingButtonClick = this.backUserSettingButtonClick.bind(this)
    }

    onChangeMailAddress(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({inputMailAddressValue: event.target.value})
        if (!this.state.inputMailAddressConfirmValue) {
            return
        }
        if (event.target.value !== '' &&
            this.state.inputMailAddressConfirmValue !== '') {
            this.setState({changeMailAddressButtonDispFlg: true})
        } else {
            this.setState({changeMailAddressButtonDispFlg: false})
        }

    }

    onChangeMailAddressConfirm(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({inputMailAddressConfirmValue: event.target.value})
        if (!this.state.inputMailAddressValue) {
            return
        }
        if (event.target.value !== '' &&
            this.state.inputMailAddressValue !== '') {
            this.setState({changeMailAddressButtonDispFlg: true})
        } else {
            this.setState({changeMailAddressButtonDispFlg: false})
        }
    }


    // 送信する
    async changeMailAddressButtonClick(): Promise<void> {
        await this.clearMessage()

        // 相関チェック
        if (this.state.inputMailAddressConfirmValue !== this.state.inputMailAddressValue) {
            this.setFieldErrorMessage('mailAddressConfirm', Messages.EOPCC0032.toString())
            return
        }
        await this.props.eventEmitter.showLoading()
        try {
            // トークン登録&メール送信
            const registerTokenRequest: RegisterTokenMailAddressRequest = {
                mailAddress: this.state.inputMailAddressValue,
                mailAddressCategoryFlg: this.props.location.state.mailAddressType,
            }

            const ret: Nullable<NatPostTokenMailAddress> = await NatAsyncCallFunction(
                this.props.tokenRepo.registerTokenMailAddress.bind(
                    this.props.tokenRepo,
                    registerTokenRequest), this.props.navigate)

            if (ret) {
                this.props.navigate(
                    `/resident/changeMailAddressMailSend`,
                )
            }
        } catch (HttpError) {
            const errorResponseBody: Nullable<ErrorResponseBody> = (HttpError as BaseHttpError).responseBody
            if (errorResponseBody !== null) {
                errorResponseBody.requestItemErrors.forEach((item: RequestItemError) => {
                    if (item.fields) {
                        item.fields.forEach((field) => {
                            this.setFieldErrorMessage(field, item.message)
                        })
                    }
                    if (item.resultCode) {
                        this.setFieldErrorMessage(item.resultCode, item.message)
                    }
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    };

    // 個人設定のページへ戻る
    backUserSettingButtonClick(): void {
        this.props.navigate(
            `/resident/personalSetting`,
        )
    };

    // メッセージ設定（フィールド）
    setFieldErrorMessage(field: string, message: string): void {
        if (field === 'mailAddress' || field === 'mailAddressConfirm') {

            let error: Error[] | undefined = this.state.fieldsError[field]
            if (error === undefined) {
                error = [{message: message, hasError: true}]
            } else {
                error.push({message: message, hasError: true})
            }
            this.setState(({fieldsError}) => (
                {
                    fieldsError: {
                        ...fieldsError,
                        [field]: error,
                    },
                }))
        }
    }

    // メッセージ取得（フィールド）
    getFieldErrorMessage(fieldId: Field): Nullable<ReactElement> {
        const field: Error[] | undefined = this.state.fieldsError[fieldId]
        if (field !== undefined) {
            return (
                <>
                    {field.map((error, index) => (
                        <FieldErrorLabel key={index}>
                            {error.message}
                        </FieldErrorLabel>
                    ))}
                </>
            )
        } else {
            return null
        }
    }

    // フィールドエラー状態取得
    getFieldErrorStatus(fieldId: Field): boolean {
        const field: Error[] | undefined = this.state.fieldsError[fieldId]
        return !!(field !== undefined && field[0] !== undefined && field[0].hasError)
    }

    // メッセージクリア
    clearMessage(): void {
        this.setState({fieldsError: {}})
    }


    render(): ReactElement {
        return (
            <>
                <ContentsSubTitleWrapper>
                    <ContentsSubTitleInner>
                        メールアドレスの修正
                    </ContentsSubTitleInner>
                </ContentsSubTitleWrapper>
                <MainContainer>
                    <MessageBlock>
                        <Message>
                            メールアドレス変更手続きのご案内メールを送信します。<br/><br/>
                            変更後の(新しい)メールアドレス宛に、変更手続きのご案内メールを送信します。<br/>
                            下記にメールアドレスを入力後、送信ボタンを押してください。<br/>
                            ※変更手続きは、変更前のご登録メールアドレスでログインした状態で行ってください。
                        </Message>
                    </MessageBlock>

                    <DisplayBlockDiv>
                        <DisplayItemInputLabelRequired>
                            <DisplayItemInputTitleRequired>新しいメールアドレス</DisplayItemInputTitleRequired>
                            <DisplayItemInput
                                type="text"
                                name="mailAddress"
                                defaultValue={this.state.inputMailAddressValue}
                                maxLength={100}
                                onChange={this.onChangeMailAddress}
                                hasError={this.getFieldErrorStatus('mailAddress')}
                                autoFocus={true}
                            />
                        </DisplayItemInputLabelRequired>
                        {this.getFieldErrorMessage('mailAddress')}
                    </DisplayBlockDiv>
                    <DisplayBlockDiv>
                        <DisplayItemInputLabelRequired>
                            <DisplayItemInputTitleRequired>新しいメールアドレス(確認用)</DisplayItemInputTitleRequired>
                            <DisplayItemInput
                                type="text"
                                name="mailAddressConfirm"
                                defaultValue={this.state.inputMailAddressConfirmValue}
                                maxLength={100}
                                onChange={this.onChangeMailAddressConfirm}
                                hasError={this.getFieldErrorStatus('mailAddressConfirm')}
                            />
                        </DisplayItemInputLabelRequired>
                        {this.getFieldErrorMessage('mailAddressConfirm')}
                    </DisplayBlockDiv>
                    <Box pb={'15px'}>
                        <TxtBold>【注意事項】</TxtBold>
                        <NoteArea>
                            <NoteList>メールが届かない場合は、ご契約のプロバイダやメールソフトの設定などにより、受信ができない可能性がございます。</NoteList>
                            <NoteList>迷惑メールの設定・ドメイン指定受信をご確認ください。</NoteList>
                        </NoteArea>
                    </Box>
                    <DisplayBlockDiv>
                        <SecondaryButton
                            onClick={this.backUserSettingButtonClick}>個人設定のページへ戻る</SecondaryButton>
                    </DisplayBlockDiv>
                    <DisplayBlockDiv>
                        <PrimaryStyledButton
                            onClick={this.changeMailAddressButtonClick}
                            disabled={!this.state.changeMailAddressButtonDispFlg}>
                            メールアドレスを変更する
                        </PrimaryStyledButton>
                    </DisplayBlockDiv>
                </MainContainer>
            </>
        )
    }
}

export default createResidentPage(NatResidentChangeMailAddressPage)

// メッセージブロック
const MessageBlock: StyledDiv = styled.div`
  text-align: center;
`
// メッセージ
const Message = styled(ParagraphText)`
  display: inline-block;
  text-align: left;
`
// 注意事項
const NoteArea: StyledDiv = styled.div`
  color: ${Colors.Alert};
`
const NoteList: StyledLi = styled.li`
  list-style-position: inside;
  list-style: none;
`
